import { useMutation } from '@apollo/client'
import styled from '@emotion/styled'
import { Edit } from '@mui/icons-material'
import {
    Avatar,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControl,
    Grid,
    IconButton,
    Skeleton,
    Typography,
} from '@mui/material'
import FileUploadButton from 'candidate/components/FileUploadButton'
import ToggleEditTextField from 'candidate/components/ToggleEditFormFields/ToggleEditTextField'
import { UpdateCandidateBioMutation } from 'candidate/graphql/candidate.gql'
import { BioFormValues } from 'candidate/models/profile'
import config from 'config'
import { Field, FieldProps, Form, Formik } from 'formik'
import {
    Dispatch,
    FC,
    SetStateAction,
    createRef,
    useEffect,
    useState,
} from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { useDispatch, useSelector } from 'react-redux'
import useToast from 'shared/components/Toast/useToast'
import { selectUser, setUser } from 'shared/state/userSlice'
import { getPxAsRem } from 'shared/utils'
import { isValidLinkedInUrl } from 'shared/utils/validation'
import * as Yup from 'yup'
import { EditButtonContainer, TitleContainer } from '../../components/styles'
import { PROFILE_SECTIONS } from './types'

const AvatarContainer = styled.div`
    display: flex;
    gap: ${getPxAsRem(16)};
    align-items: center;
    margin: ${getPxAsRem(16, 0, 24)};
`

const StyledDialogContent = styled(DialogContent)`
    padding: ${getPxAsRem(32)};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${getPxAsRem(24)};
    min-width: ${getPxAsRem(300)};
    min-height: ${getPxAsRem(300)};
`

const DialogButtonContainer = styled(FormControl)`
    display: flex;
    gap: ${getPxAsRem(8)};
    flex-direction: row;
`

interface Props {
    loading?: boolean
    id: string
    email: string
    phone: string
    profileImageKey: string
    linkedinUrl: string
    refetch: () => Promise<void>
    edit: boolean
    setEdit: (nextState: PROFILE_SECTIONS | null) => void
    closeEditWithoutModal: () => void
    setUnsavedChangesExist: Dispatch<SetStateAction<boolean>>
}

const Bio: FC<Props> = ({
    loading = false,
    id,
    email,
    phone,
    profileImageKey,
    linkedinUrl,
    refetch,
    edit,
    setEdit,
    closeEditWithoutModal,
    setUnsavedChangesExist,
}) => {
    const toast = useToast()
    const dispatch = useDispatch()
    const user = useSelector(selectUser)

    // profile image
    const [dialogOpen, setDialogOpen] = useState(false)
    const [image, setImage] = useState('')
    const [imageFile, setImageFile] = useState<File>()
    const inputRef = createRef<HTMLInputElement>()
    const [imageLoading, setImageLoading] = useState(false)

    const [profileImage, setProfileImage] = useState<string | undefined>(
        profileImageKey ?? undefined
    )
    const bioValidationSchema = Yup.object().shape({
        phone: Yup.string()
            .required('Required')
            .test('phone', 'Invalid phone number', (value) => {
                if (value) {
                    return isValidPhoneNumber(value)
                }
                return true
            }),
        linkedinUrl: Yup.string().test(
            'linkedinUrl',
            'Invalid LinkedIn URL',
            (value) => {
                if (value) {
                    return isValidLinkedInUrl(value)
                }
                return true
            }
        ),
    })

    const [updateBio] = useMutation(UpdateCandidateBioMutation, {
        onCompleted() {
            refetch()
            closeEditWithoutModal()
            toast({
                title: 'Success',
                message: 'Your bio has been updated',
                severity: 'success',
            })
        },
        onError(error) {
            console.error('bio update error: ', error)
            // TODO: more detailed error message?
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        },
    })

    useEffect(() => {
        if (profileImageKey) {
            setProfileImage(profileImageKey)
        }
    }, [profileImageKey])

    // profile pic handlers
    const handleSelectFile = (e) => {
        const file = e.target.files[0]
        if (file) {
            setImageFile(file)
            setImage(URL.createObjectURL(file))
            setDialogOpen(true)
        }
    }
    const handleUpload = () => {
        if (imageFile) {
            setImageLoading(true)
            saveImage(imageFile)
        }
    }

    const closeDialog = () => {
        setDialogOpen(false)
        setImageLoading(false)
        if (inputRef.current) inputRef.current.value = ''
    }

    // form handlers
    const handleSubmit = (values: BioFormValues) => {
        updateBio({
            variables: {
                id: id,
                phone: values.phone,
                linkedinUrl: values.linkedinUrl,
            },
        })
    }

    const handleEditClick = () => {
        if (edit) {
            closeEditWithoutModal()
        } else {
            setEdit(PROFILE_SECTIONS.BIO)
        }
    }

    return (
        <>
            <TitleContainer>
                <Typography variant="h6" component="h2">
                    Bio
                </Typography>
                <IconButton onClick={handleEditClick} disabled={loading}>
                    <Edit fontSize="small" />
                </IconButton>
            </TitleContainer>
            {loading ? (
                <AvatarContainer>
                    <Skeleton variant="circular">
                        <Avatar />
                    </Skeleton>
                    <Skeleton>
                        <Button variant="text">Change</Button>
                    </Skeleton>
                </AvatarContainer>
            ) : (
                <AvatarContainer>
                    <Avatar src={profileImage} />
                    <FileUploadButton
                        onChange={handleSelectFile}
                        ref={inputRef}
                        label="Change"
                    />
                </AvatarContainer>
            )}
            <Formik
                initialValues={{
                    phone,
                    linkedinUrl,
                }}
                enableReinitialize={true}
                onSubmit={handleSubmit}
                validationSchema={bioValidationSchema}
                validateOnBlur
                style={{ width: '100%' }}
            >
                {({ isSubmitting, errors, touched, resetForm, dirty }) => {
                    if (dirty && !edit) resetForm()
                    if (edit) setUnsavedChangesExist(dirty)
                    return (
                        <Form>
                            <Grid
                                container
                                rowSpacing={3}
                                columnSpacing={2}
                                sx={{
                                    flexDirection: {
                                        xs: 'column',
                                        md: 'row',
                                    },
                                }}
                            >
                                <Grid item xs={6}>
                                    {/* email field is not editable at this time */}
                                    <Field name="email">
                                        {({ field }: FieldProps<string>) => (
                                            <ToggleEditTextField
                                                {...field}
                                                edit={edit}
                                                disabled
                                                label="Personal Email"
                                                value={email}
                                                loading={loading}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item xs={6}>
                                    <Field name="phone">
                                        {({ field }: FieldProps<string>) => (
                                            <ToggleEditTextField
                                                {...field}
                                                id="phone"
                                                edit={edit}
                                                label="Personal Phone"
                                                loading={loading}
                                                error={
                                                    !!errors.phone &&
                                                    touched.phone
                                                }
                                                mask="formikPhone"
                                                helperText={
                                                    !!errors.phone &&
                                                    touched.phone
                                                        ? errors.phone
                                                        : undefined
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item xs={6}>
                                    <Field name="linkedinUrl">
                                        {({ field }: FieldProps<string>) => (
                                            <ToggleEditTextField
                                                {...field}
                                                id="linkedinUrl"
                                                edit={edit}
                                                label="LinkedIn URL"
                                                loading={loading}
                                                error={
                                                    !!errors.linkedinUrl &&
                                                    touched.linkedinUrl
                                                }
                                                helperText={
                                                    !!errors.linkedinUrl &&
                                                    touched.linkedinUrl
                                                        ? errors.linkedinUrl
                                                        : undefined
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                {edit && (
                                    <EditButtonContainer item xs={12}>
                                        <Button
                                            variant="outlined"
                                            type="reset"
                                            onClick={() => {
                                                resetForm()
                                                closeEditWithoutModal()
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            disabled={
                                                !dirty ||
                                                isSubmitting ||
                                                !!errors.phone ||
                                                !!errors.linkedinUrl
                                            }
                                            endIcon={
                                                isSubmitting ? (
                                                    <CircularProgress
                                                        size={14}
                                                    />
                                                ) : undefined
                                            }
                                        >
                                            Save and Update
                                        </Button>
                                    </EditButtonContainer>
                                )}
                            </Grid>
                        </Form>
                    )
                }}
            </Formik>
            <Dialog
                open={dialogOpen}
                onClose={!imageLoading ? closeDialog : undefined}
            >
                {imageLoading ? (
                    <StyledDialogContent>
                        <CircularProgress />
                        <Typography variant="h6" component="h1">
                            Uploading new profile picture
                        </Typography>
                    </StyledDialogContent>
                ) : (
                    <StyledDialogContent>
                        <Avatar src={image} sx={{ width: 180, height: 180 }} />
                        <DialogButtonContainer>
                            <Button
                                variant="outlined"
                                onClick={closeDialog}
                                type="button"
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleUpload}>Upload Image</Button>
                        </DialogButtonContainer>
                    </StyledDialogContent>
                )}
            </Dialog>
        </>
    )

    async function saveImage(image: File): Promise<void> {
        const formData = new FormData()
        formData.append('picture', image)

        try {
            const response = await fetch(`${config.api.uri}/profilePicture`, {
                method: 'POST',
                body: formData,
                headers: {
                    accept: 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                    contentType: 'multipart/form-data',
                    boundary: '----WebKitFormBoundary7MA4YWxkTrZu0gW',
                },
            })
            const data = await response.json()
            if (response.status === 200 && data?.signedUrl) {
                setProfileImage(data.signedUrl)
                dispatch(
                    setUser({
                        ...user,
                        profileImageKey: data.signedUrl,
                    })
                )
                toast({
                    title: 'Profile image updated',
                    message: '',
                    severity: 'success',
                })
            } else {
                console.error('image upload error: ', data)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            }
        } catch (error) {
            console.error('image upload error: ', error)
            // TODO: more detailed error message?
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        } finally {
            closeDialog()
        }
    }
}

export default Bio
